import { isEmpty } from "lodash";
import React, { useCallback } from "react";
import { GQLEventSearchBlock } from "../../../utils/types";

import { Search } from "@clockwise/icons";
import { useAIMessageContext } from "../../../hooks/AIMessageContext";
import {
  AttendeeFilter,
  OrdinalModifier,
  RsvpFilters,
  TagFilters,
  TimeRangeFilters,
  TitleFilter,
} from "./filters";

const IN_ORDER_SEARCH_FILTERS = [
  "ordinalModifier",
  "timeRangeFilter",
  "eventIdFilters",
  "titleFilter",
  "attendeeFilters",
  "tagFilters",
  "rsvpFilters",
];

export const EventSearchBlock = ({
  eventSearchBlock,
  conversationId,
}: {
  eventSearchBlock: NonNullable<GQLEventSearchBlock>;
  conversationId: string;
}) => {
  const { processingMessage } = useAIMessageContext();

  const renderComponentForFilter = useCallback(
    (filter: string) => {
      switch (filter) {
        case "ordinalModifier":
          if (!!eventSearchBlock.ordinalModifier) {
            return (
              <OrdinalModifier
                ordinal={eventSearchBlock.ordinalModifier.ordinal}
                modifier={eventSearchBlock.ordinalModifier.modifier}
                disabled={processingMessage}
                eventSearchBlockId={eventSearchBlock.id}
                conversationId={conversationId}
              />
            );
          }
          return null;
        case "tagFilters":
          if (!isEmpty(eventSearchBlock.tagFilters)) {
            return (
              <TagFilters
                tags={eventSearchBlock.tagFilters}
                eventSearchBlockId={eventSearchBlock.id}
                disabled={processingMessage}
                conversationId={conversationId}
              />
            );
          }
          return null;
        case "rsvpFilters":
          if (!isEmpty(eventSearchBlock.rsvpFilters) && eventSearchBlock.rsvpFilters.length < 3) {
            return (
              <RsvpFilters
                rsvpFilters={eventSearchBlock.rsvpFilters}
                eventSearchBlockId={eventSearchBlock.id}
                disabled={processingMessage}
                conversationId={conversationId}
              />
            );
          }
          return null;
        case "timeRangeFilter":
          if (!!eventSearchBlock.timeRangeFilter) {
            return (
              <TimeRangeFilters
                included={eventSearchBlock.timeRangeFilter.included}
                excluded={eventSearchBlock.timeRangeFilter.excluded}
                eventSearchBlockId={eventSearchBlock.id}
                disabled={processingMessage}
                conversationId={conversationId}
              />
            );
          }
          return null;
        case "titleFilter":
          if (!!eventSearchBlock.titleFilter) {
            return (
              <TitleFilter
                modifier={eventSearchBlock.titleFilter.modifier}
                title={eventSearchBlock.titleFilter.title}
                eventSearchBlockId={eventSearchBlock.id}
                disabled={processingMessage}
                conversationId={conversationId}
              />
            );
          }
          return null;
        case "attendeeFilters":
          if (!!eventSearchBlock.attendeeFilter) {
            return (
              <AttendeeFilter
                attendeeFilter={eventSearchBlock.attendeeFilter}
                eventSearchBlockId={eventSearchBlock.id}
                disabled={processingMessage}
                conversationId={conversationId}
              />
            );
          }
          return null;
        default:
          return null;
      }
    },
    [
      eventSearchBlock.ordinalModifier,
      eventSearchBlock.attendeeFilter,
      eventSearchBlock.rsvpFilters,
      eventSearchBlock.tagFilters,
      eventSearchBlock.timeRangeFilter,
      eventSearchBlock.titleFilter,
      processingMessage,
    ],
  );

  return (
    <div className="cw-w-full cw-rounded-xl cw-bg-neutral-inset cw-gap-0.5 cw-flex cw-flex-wrap cw-py-1 cw-px-1.5">
      <div className="cw-text-subtle cw-flex cw-items-center cw-mr-0.5">
        <Search className="cw-h-4 cw-w-4" />
      </div>
      {IN_ORDER_SEARCH_FILTERS.map((filter) => {
        return <React.Fragment key={filter}>{renderComponentForFilter(filter)}</React.Fragment>;
      })}
    </div>
  );
};
